import { useState } from 'react'
import { useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { Button } from '@factorialco/factorial-one-react/'
import {
  Alert,
  AlertDescription,
  AlertTitle,
  BasicTextEditorHandle,
  Tabs,
} from '@factorialco/factorial-one-react/dist/experimental'
import { ArrowLeft } from '@factorialco/factorial-one-react/icons/app'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { buildUrl } from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { AIPageChat } from 'modules/meetings/components/AIPageChat'
import { TranscriptMessages } from 'modules/meetings/components/TranscriptMesssages'
import { useMeetingDetails } from 'modules/meetings/lib/hooks/useMeetingDetails'
import { useMeetingTranscript } from 'modules/meetings/lib/hooks/useMeetingTranscript'

interface PostMeetingSideBarProps {
  textEditorRef: React.RefObject<BasicTextEditorHandle> | null | undefined
}

export const PostMeetingSideBar = ({ textEditorRef }: PostMeetingSideBarProps) => {
  const [activeTab, setActiveTab] = useState<'transcript' | 'chat'>('transcript')
  const { transcript } = useMeetingTranscript()
  const { meeting } = useMeetingDetails()
  const {
    access: { locale },
  } = useCurrentSession()
  const attendees = meeting?.attendees || []

  const { id } = useParams<{ id: string }>()

  const users =
    attendees?.map((attendee) => ({
      id: String(attendee.id),
      fullname: attendee.employee?.fullName || i18n.t('meetings.unknown_participant'),
      imageUrl: buildUrl(attendee.employee?.avatar?.url),
    })) || []

  const messages =
    transcript?.map((utterance) => {
      const timestamp = moment(utterance?.timestamp, 'x').locale(locale).toISOString()
      return {
        userId: String(utterance?.attendeeId),
        text: utterance?.utterance || '',
        dateTime: timestamp || '',
      }
    }) || []

  const addTranscript = () => {
    try {
      textEditorRef?.current?.insertTranscript(
        i18n.t('meetings.meeting.transcript.note_transcript_title'),
        users,
        messages
      )
      app.setFlash({
        type: 'correct',
        message: i18n.t('meetings.meeting.transcript.add_transcript_success'),
      })
    } catch (error) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error'),
      })
    }
  }

  return (
    <Box
      style={{ width: '100%', maxWidth: '500px' }}
      height="full"
      border={{
        left: {
          width: 's1',
          style: 'solid',
          color: 'grey300',
        },
      }}
      paddingTop="s12"
    >
      <Tabs
        activeTabId={activeTab}
        tabs={[
          {
            index: true,
            id: 'transcript',
            label: i18n.t('meetings.meeting.transcript.title'),
            onClick: () => setActiveTab('transcript'),
          },
          {
            id: 'chat',
            label: i18n.t('meetings.meeting.chat.title'),
            onClick: () => setActiveTab('chat'),
          },
        ]}
      />

      {activeTab === 'transcript' && (
        <Box height="full" flexGrow overflowY="auto" gap="s12">
          <TranscriptMessages>
            <Alert variant="warning">
              <AlertTitle>
                {i18n.t('meetings.meeting.transcript.transcript_warning_title')}
              </AlertTitle>
              <AlertDescription>
                {i18n.t('meetings.meeting.transcript.transcript_warning_description')}
              </AlertDescription>
            </Alert>
          </TranscriptMessages>
          <Box flexDirection="row" paddingX="s24" paddingBottom="s12">
            <Button
              onClick={addTranscript}
              label={i18n.t('meetings.meeting.transcript.add_transcript')}
              variant="outline"
              icon={ArrowLeft}
            />
          </Box>
        </Box>
      )}
      {activeTab === 'chat' && id && (
        <AIPageChat meetingId={Number(id)} textEditorRef={textEditorRef} />
      )}
    </Box>
  )
}
